import { useCallback, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'

import { aiApi } from '../services/api'

// Requests AI suggested bets for the composer. Suggestions run only when the
// user clicks the card's "Find bets from this post" button, which calls
// requestSuggestedBets; nothing requests them on its own (#870). Only the
// newest request's response is applied to aiReview: a slower, older response
// is dropped.
export function useSuggestedBets({
  currentOrg,
  setAiReview,
  // The regulations the composer's market search covers when a request is
  // sent; suggestions search the same ones (none: the organization default).
  searchOfferingsRef,
}) {
  const mutationRef = useRef(null)
  const requestSequenceRef = useRef(0)

  const requestSuggestedBets = useCallback((payload) => {
    const requestId = ++requestSequenceRef.current
    mutationRef.current?.mutate({ ...payload, __requestId: requestId })
  }, [])

  const mutation = useMutation({
    mutationFn: ({ __source: _source, __requestId: _requestId, ...payload }) => {
      const offeringIds = searchOfferingsRef?.current || []
      return aiApi.suggestBets(currentOrg.slug, offeringIds.length ? { ...payload, offering_ids: offeringIds } : payload)
    },
    onSuccess: (data, variables) => {
      if (variables.__requestId !== requestSequenceRef.current) return
      const source = variables.__source || 'text'
      setAiReview((currentValue) => ({
        ...currentValue,
        suggestedBets: Array.isArray(data?.suggestions) ? data.suggestions : [],
        // What the model read that no market matched, and why a search failed:
        // the card says so instead of "No bets found" (#870).
        suggestedBetsUnmatched: Array.isArray(data?.unmatched) ? data.unmatched : [],
        suggestedBetsWarnings: Array.isArray(data?.warnings) ? data.warnings : [],
        suggestedBetsSource: source,
        generatedAt: new Date().toISOString(),
      }))
    },
    onError: () => {},
  })
  mutationRef.current = mutation

  return {
    requestSuggestedBets,
    suggestBetsMutation: mutation,
  }
}
